// Audio asset upload: the audio counterpart of glbUpload, through the same
// presign -> PUT -> confirm pipeline. Duration is decoded client-side and
// rides in the asset meta for the Audio mode's library listing.
import type { StudioAssetRecord } from '../../engine';
import { apiPost, apiUpload } from '../services/api';
import { AssetValidationError } from './glbUpload';

export const MAX_AUDIO_BYTES = 5 * 1024 * 1024;

/** Upload content types by file extension; mirrors the API allowlist. */
const AUDIO_TYPES: Record<string, string> = {
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  wav: 'audio/wav',
};

function contentTypeFor(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const contentType = AUDIO_TYPES[ext];
  if (!contentType) {
    throw new AssetValidationError('Only .mp3, .ogg and .wav audio is supported.');
  }
  if (file.size > MAX_AUDIO_BYTES) {
    throw new AssetValidationError(
      `Audio is ${(file.size / 1024 / 1024).toFixed(1)} MiB; the limit is 5 MiB.`,
    );
  }
  return contentType;
}

/** Decode the file to read its duration; 0 when it cannot be decoded. */
async function readDuration(buffer: ArrayBuffer): Promise<number> {
  const Ctor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return 0;
  const context = new Ctor();
  try {
    const decoded = await context.decodeAudioData(buffer.slice(0));
    return Number(decoded.duration.toFixed(2));
  } catch {
    return 0;
  } finally {
    void context.close().catch(() => undefined);
  }
}

interface PresignWire {
  upload_url: string;
  asset_id: string;
  s3_key: string;
  cf_url: string;
}

interface ConfirmedAssetWire {
  id: string;
  name: string;
  cf_url: string;
  file_size: number;
  created_at: string;
}

/**
 * Validate an audio file, extract its duration, request a presigned URL,
 * upload, confirm, and return a StudioAssetRecord.
 */
export async function uploadAudioAsset(projectId: string, file: File): Promise<StudioAssetRecord> {
  const contentType = contentTypeFor(file);
  const durationSeconds = await readDuration(await file.arrayBuffer());

  const presign = await apiPost<PresignWire>(`/projects/${projectId}/assets/presign`, {
    name: file.name,
    file_size: file.size,
    content_type: contentType,
  });

  await apiUpload(presign.upload_url, file, contentType);

  const confirmed = await apiPost<ConfirmedAssetWire>(`/projects/${projectId}/assets/confirm`, {
    asset_id: presign.asset_id,
    name: file.name.replace(/\.(mp3|ogg|wav)$/i, ''),
    file_size: file.size,
    content_type: contentType,
    s3_key: presign.s3_key,
    cf_url: presign.cf_url,
    meta: { durationSeconds },
  });

  return {
    id: confirmed.id,
    name: confirmed.name,
    kind: 'audio',
    storagePath: presign.s3_key,
    downloadUrl: confirmed.cf_url,
    bounds: { size: [1, 1, 1], center: [0, 0, 0] },
    category: 'structural',
    sizeBytes: confirmed.file_size,
    createdAt: new Date(confirmed.created_at).getTime(),
    durationSeconds,
  };
}
